import React from "react";
import { PlusIcon } from "../../../assets/Icons/PlusIcon";
import { MinusIcon } from "../../../assets/Icons/MinusIcon";
import NavArrowUp from "../../../assets/Icons/NavArrowUp";
import { NavDownArrow } from "../../../assets/Icons/NavArrowDown";
import {
  isOtherSectionWideField,
  normalizeFieldIdentifier,
  getOrderedOtherSectionFields,
} from "../orderDetailsFieldConstants";

/**
 * OtherTab Component
 * Displays and manages the Other tab content (Efforts & Savings sections)
 * Handles repeated rows with complex field management and section expansion
 * 
 * Props:
 * - currentTabSections: Array of section objects with filtered/resolved rows
 * - isEditing: Boolean indicating edit mode
 * - expandedSections: Object tracking which sections are expanded
 * - onToggleSectionExpansion: Callback to toggle section expansion
 * - renderFieldInput: Function to render individual field inputs
 * - errorFieldArray: Array of field keys with errors
 * - onAddRow: Callback to add a new row to a section
 * - onRemoveRow: Callback to remove a row from a section
 */
const OtherTab = ({
  currentTabSections,
  isEditing,
  expandedSections,
  onToggleSectionExpansion,
  renderFieldInput,
  errorFieldArray,
  onAddRow,
  onRemoveRow,
}) => {
  // Check if there are any sections with records
  if (!currentTabSections || currentTabSections.length === 0) {
    return (
      <div className="orders-empty-state">
        <p>No Efforts or Savings records available.</p>
      </div>
    );
  }

  return (
    <div className="sections-container">
      {currentTabSections.map((section) => (
        <div key={section.sectionName} className="accordion-section">
          {/* SECTION HEADER */}
          <div
            className={`accordion-header ${
              expandedSections[section.sectionName] ? "expanded" : ""
            }`}
          >
            <button
              type="button"
              className="accordion-header-toggle"
              onClick={() => onToggleSectionExpansion(section.sectionName)}
            >
              <span className="accordion-title">{section.sectionName}</span>
            </button>
            <div className="accordion-header-actions">
              {isEditing && (
                <button
                  type="button"
                  className="accordion-add-button"
                  aria-label={`Add ${section.sectionName} row`}
                  onClick={() => onAddRow(section.sectionName)}
                >
                  <PlusIcon width={16} height={16} color="#DEDEE2" />
                </button>
              )}
              <button
                type="button"
                className="accordion-expand-button"
                aria-label={`${
                  expandedSections[section.sectionName] ? "Collapse" : "Expand"
                } ${section.sectionName}`}
                onClick={() => onToggleSectionExpansion(section.sectionName)}
              >
                <span className="accordion-icon">
                  {expandedSections[section.sectionName] ? (
                    <NavArrowUp width={16} height={16} color="#A4A3B1" />
                  ) : (
                    <NavDownArrow width={16} height={16} color="#A4A3B1" />
                  )}
                </span>
              </button>
            </div>
          </div>

          {/* SECTION CONTENT */}
          {expandedSections[section.sectionName] && (
            <div className="accordion-content">
              {section.rows.length === 0 ? (
                <div className="other-section-no-records">
                  <p>
                    No records found.{" "}
                    {isEditing ? "Click + to add a new record." : ""}
                  </p>
                </div>
              ) : (
                section.rows.map((row) => {
                  const sectionGridSlug = normalizeFieldIdentifier(
                    section.sectionName,
                  );
                  const renderedRowFields = isEditing
                    ? getOrderedOtherSectionFields(
                        section.sectionName,
                        row.fields,
                      )
                    : row.fields;

                  if (renderedRowFields.length === 0) {
                    return null;
                  }

                  return (
                    <div
                      key={row.rowKey}
                      className={`details-grid-group ${
                        isEditing ? "other-section-row" : ""
                      }`}
                    >
                      <div
                        className={`details-grid ${
                          isEditing
                            ? `other-section-grid other-section-grid-${sectionGridSlug}`
                            : ""
                        }`}
                      >
                        {renderedRowFields.map((field, index) => (
                          <div
                            key={`${section.sectionName}-${row.rowKey}-${
                              field.field || field.fieldStateKey
                            }-${index}`}
                            className={`details-item ${
                              isEditing &&
                              isOtherSectionWideField(section.sectionName, field)
                                ? "other-section-item-full-width"
                                : ""
                            }`}
                          >
                            <div className="label">
                              {field.labelToShowOnUI}
                              {field.required && (
                                <span className="required-indicator">*</span>
                              )}
                            </div>
                            <div className="field-container">
                              {renderFieldInput(field)}
                            </div>
                          </div>
                        ))}
                      </div>
                      {isEditing && (
                        <button
                          type="button"
                          className="accordion-remove-button"
                          aria-label={`Remove ${section.sectionName} row`}
                          onClick={() =>
                            onRemoveRow(section.sectionName, row.rowKey)
                          }
                        >
                          <MinusIcon width={16} height={16} color="#DEDEE2" />
                          <span>Delete</span>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default OtherTab;
